import { Config } from "../../Config";

export interface SseAction {
  type: string;
  data?: Record<string, unknown>;
}

export interface OpenSseStreamCallbacks {
  onDelta: (delta: string) => void;
  onDone: (
    botMessageId: string,
    fullText: string,
    creationDate: Date,
    action?: SseAction,
    documentUrl?: string,
    documentName?: string
  ) => void;
  onError: (code: string) => void;
  onThought?: (text: string) => void;
}

/**
 * Opens an SSE stream for a chat message using fetch + ReadableStream.
 *
 * EventSource cannot send custom headers, so the Authorization bearer token
 * can't be attached — every request would be rejected with 401. This
 * implementation uses fetch instead, which supports arbitrary headers.
 *
 * @param chatId        The chat the message belongs to.
 * @param userMessageId The user message id returned by the POST endpoint.
 * @param callbacks     Handlers for delta, done, and error events.
 * @param token         Bearer token from the auth state (same value used by useApi).
 */
export const openSseStream = (
  chatId: string,
  userMessageId: string,
  callbacks: OpenSseStreamCallbacks,
  token: string | undefined
): { close: () => void } => {
  const controller = new AbortController();
  const url = `${Config.API_URL}/conversations/${chatId}/stream/${userMessageId}`;

  const headers: Record<string, string> = {
    Accept: "text/event-stream",
  };
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const IDLE_TIMEOUT_MS = 130_000;
  let timeoutId = window.setTimeout(() => {
    callbacks.onError("client_timeout");
    controller.abort();
  }, IDLE_TIMEOUT_MS);

  const resetIdleTimeout = () => {
    clearTimeout(timeoutId);
    timeoutId = window.setTimeout(() => {
      callbacks.onError("client_timeout");
      controller.abort();
    }, IDLE_TIMEOUT_MS);
  };

  fetch(url, {
    method: "GET",
    headers,
    signal: controller.signal,
  })
    .then(async (res) => {
      if (!res.ok || !res.body) {
        callbacks.onError("http_error");
        return;
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        resetIdleTimeout();
        buf += decoder.decode(value, { stream: true });

        let idx: number;
        while ((idx = buf.indexOf("\n\n")) !== -1) {
          const frame = buf.slice(0, idx);
          buf = buf.slice(idx + 2);
          handleFrame(frame, callbacks);
        }
      }
    })
    .catch((err: unknown) => {
      if (err instanceof Error && err.name === "AbortError") return;
      callbacks.onError("connection_lost");
    });

  return {
    close: () => {
      clearTimeout(timeoutId);
      controller.abort();
    },
  };
};

function handleFrame(frame: string, callbacks: OpenSseStreamCallbacks): void {
  const lines = frame.split("\n");
  let event: string | undefined;
  let data: string | undefined;

  for (const line of lines) {
    if (line.startsWith("event: ")) {
      event = line.slice(7).trim();
    } else if (line.startsWith("data: ")) {
      data = (data ?? "") + line.slice(6);
    }
  }

  if (!event) return;

  try {
    const parsed = data ? JSON.parse(data) : {};
    if (event === "message:thought") {
      callbacks.onThought?.(parsed.text);
    } else if (event === "message:delta") {
      callbacks.onDelta(parsed.delta);
    } else if (event === "message:done") {
      callbacks.onDone(
        parsed.botMessageId,
        parsed.fullText,
        new Date(parsed.creationDate),
        parsed.action,
        parsed.documentUrl,
        parsed.documentName
      );
    } else if (event === "stream:error") {
      callbacks.onError(parsed.code ?? "unknown");
    }
    // stream:connected and stream:done are informational; no callback needed.
  } catch {
    /* ignore parse errors */
  }
}
